import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import type { Analysis, CustomizedBlock } from 'venn-api';
import type { VennColors } from 'venn-ui-kit';
import { type CustomBlockTypeEnum } from 'venn-utils';
import { useAgGridStyle, type AnalysisRequest, type AnalysisResponseParser } from '../../../studio-blocks';
import { useMetaRows } from './use-meta-rows';
import { useGetRequestSubjectFee } from './use-advisory-fee';

const useAnalysisGridRowData = (
  requests: AnalysisRequest[],
  responseParser: AnalysisResponseParser,
  analysesGroup: (Analysis | undefined)[][],
  selectedBlock: CustomizedBlock,
) => {
  const metaRows = useMetaRows(requests);

  const rowData = useMemo(() => {
    const rows = responseParser.parser(selectedBlock, analysesGroup);
    if (rows.length === 0) {
      return metaRows;
    }

    return [...metaRows, ...rows];
  }, [responseParser, selectedBlock, analysesGroup, metaRows]);

  return rowData;
};

const useAnalysisGrid = (
  requests: AnalysisRequest[],
  responseParser: AnalysisResponseParser,
  Colors: VennColors,
  analysesGroup: (Analysis | undefined)[][],
  selectedBlock: CustomizedBlock,
  customBlockType: CustomBlockTypeEnum,
  selectedMetricIds: string[],
) => {
  const theme = useTheme();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const rowData = useAnalysisGridRowData(requests, responseParser, analysesGroup, selectedBlock);

  const metricsSettings = responseParser.metricsSettings;
  const gridStyle = useAgGridStyle();
  const columnDefs = useMemo(() => {
    return responseParser.columnParser({
      customBlockType,
      requests,
      analysesGroup,
      metricsSettings,
      theme,
      selectedBlock,
      gridStyle,
      selectedMetricIds,
      getRequestSubjectFee,
    });
  }, [
    customBlockType,
    requests,
    analysesGroup,
    metricsSettings,
    selectedBlock,
    gridStyle,
    theme,
    responseParser,
    selectedMetricIds,
    getRequestSubjectFee,
  ]);

  return {
    rowData,
    columnDefs,
  };
};

export default useAnalysisGrid;
